import { debounce } from "lodash";
import {
    PerspectiveCamera,
    Scene,
    WebGLRenderer,
    Timer,
    LinearSRGBColorSpace,
} from "three";

export default class ThreeEngine {
    constructor(canvasReference) {
        this.renderer = undefined;
        this.scene = undefined;
        this.camera = undefined;

        this.rafId = null;
        this.objects = new Set();
        this.isDestroyed = false;
        this.isMobile = window.innerWidth <= 1024;
        this.isLowPower = ThreeEngine._detectLowPower();

        this.timer = new Timer();

        this.init(canvasReference);
    }

    setup() {
        this.setupSizes();
        this.setupRenderer();
        this.setupScene();
        this.setupCamera();
        this.setupResize();

        this.rafId = requestAnimationFrame(this.animate);
    }

    setupSizes() {
        const parent = this.DOM.canvas.parentElement;

        this.sizes = {
            width: parent.clientWidth,
            height: parent.clientHeight,
            aspect: parent.clientWidth / parent.clientHeight,
        };
    }

    setupRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.DOM.canvas,
            antialias: window.devicePixelRatio <= 1,
            alpha: true,
            powerPreference: "high-performance",
        });

        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
        this.renderer.outputColorSpace = LinearSRGBColorSpace;
    }

    setupScene() {
        this.scene = new Scene();
    }

    setupCamera() {
        this.camera = new PerspectiveCamera(40, this.sizes.aspect, 0.1, 100);
        this.camera.position.set(0, 0, 10);
        this.scene.add(this.camera);
    }

    setupResize() {
        this.handleResize = debounce(this.resize, 250);
        window.addEventListener("resize", this.handleResize);
    }

    addObject = (object, newObject = true) => {
        if (!object) return;

        this.objects.add(object);

        if (newObject) {
            if (object.object3D) {
                this.scene.add(object.object3D);
            } else {
                this.scene.add(object);
            }
        }

        object.onAdd?.(this);
    };

    removeObject = (object) => {
        if (!object) return;

        this.objects.delete(object);

        if (object.object3D) {
            this.scene.remove(object.object3D);
        } else {
            this.scene.remove(object);
        }

        object.onRemove?.(this);
        object.dispose?.();
    };

    resize = () => {
        this.isMobile = window.innerWidth <= 1024;
        this.isLowPower = ThreeEngine._detectLowPower();

        this.setupSizes();

        this.camera.aspect = this.sizes.aspect;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(
            Math.min(window.devicePixelRatio, this.isLowPower ? 1.0 : 1.75),
        );

        this.objects.forEach((obj) => {
            obj.resize?.(this.sizes, this.isMobile, this.isLowPower);
        });
    };

    static _detectLowPower() {
        const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
        const lowCPU = (navigator.hardwareConcurrency ?? 8) <= 4;
        const lowRAM = (navigator.deviceMemory ?? 8) <= 4;
        return coarsePointer || (lowCPU && lowRAM);
    }

    animate = () => {
        if (this.isDestroyed) return;
        if (!this.renderer || !this.scene || !this.camera) return;

        this.timer?.update();
        const delta = this.timer.getDelta();
        const elapsed = this.timer.getElapsed();

        this.objects.forEach((obj) => {
            obj.update?.(delta, elapsed);
        });

        this.renderer.render(this.scene, this.camera);

        this.rafId = requestAnimationFrame(this.animate);
    };

    disposeObject = (object) => {
        if (!object) return;

        while (object.children?.length) {
            const child = object.children[0];
            object.remove(child);
            this.disposeObject(child);
        }

        if (object.geometry) {
            object.geometry.dispose();
        }

        if (object.material) {
            if (Array.isArray(object.material)) {
                object.material.forEach((material) =>
                    this.disposeMaterial(material),
                );
            } else {
                this.disposeMaterial(object.material);
            }
        }
    };

    disposeMaterial = (material) => {
        for (const key in material) {
            const value = material[key];
            if (value && typeof value === "object" && "minFilter" in value) {
                value.dispose?.();
            }
        }

        material.dispose?.();
    };

    disposeScene = () => {
        if (!this.scene) return;

        while (this.scene.children.length) {
            const object = this.scene.children[0];
            this.scene.remove(object);
            this.disposeObject(object);
        }
    };

    destroy() {
        this.isDestroyed = true;

        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        window.removeEventListener("resize", this.handleResize);
        this.resizeObserver?.disconnect();

        this.disposeScene();

        this.renderer?.dispose();
        this.renderer?.forceContextLoss?.();

        this.scene = null;
        this.camera = null;
        this.renderer = null;
    }

    init(canvasReference = undefined) {
        if (canvasReference?.value) {
            this.DOM = { canvas: canvasReference.value };
            this.setup();
        }
    }
}
